import { FC, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  TReportRefundsRateData,
  ReportPeriod,
  ReportGroupBy,
} from '@src/types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { getChartColor, useReportChartData } from '@hooks/useReportChartData';
import { buildCommonPayloadWithGrouping } from '@src/utils/buildCommonPayload';
import { getRefundsRate, exportRefundsRate } from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportRefundsRateData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col items-center min-w-[135px] gap-y-1"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          {payload[0]?.value}%
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.date}
        </p>
        <p className="text-white text-xs">Refunds Rate: {payload[0]?.value}%</p>
      </div>
    );
  }

  return null;
};

export const RefundsRate: FC = () => {
  const [data, setData] = useState<TReportRefundsRateData[]>([]);

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.THIS_MONTH,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
  });

  const { columns, displayItems, tableData } =
    useReportTableColumns<TReportRefundsRateData>({
      data,
      selectedItems: ['refunds_rate'],
      compareToPreviousPeriod: filters.compareToPreviousPeriod,
      compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
      getAccessField: (item) => item.date,
      getMainDataField: (item) => item.refunds_rate,
      getPreviousPeriodField: (item) => item.refunds_rate_previous,
      getPreviousYearField: (item) => item.refunds_rate_previous_year,
      includeTotalsRow: false,
      valueFormatter: (_, value: number) => `${value}%`,
      columnTitleFormatter: (key) => {
        if (key === 'refunds_rate') return 'REFUNDS RATE (%)';
        if (key === 'refunds_rate (Previous Period)')
          return 'REFUNDS RATE (%) (Previous Period)';
        if (key === 'refunds_rate (Previous Year)')
          return 'REFUNDS RATE (%) (Previous Year)';

        return key;
      },
    });

  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item) => item.refunds_rate,
    getPreviousPeriodField: (item) => item.refunds_rate_previous,
    getPreviousYearField: (item) => item.refunds_rate_previous_year,
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildCommonPayloadWithGrouping(filters);
      const response = await getRefundsRate(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Failed to generate report. Please try again.');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const payload = buildCommonPayloadWithGrouping(filters);
      const response = await exportRefundsRate(payload);

      downloadFile(response.data, 'refunds-rate-report');
      toast.success('Report exported successfully!');
    } catch (error) {
      toast.error('Failed to export report. Please try again.');
      console.error('Error exporting report:', error);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">Refunds Rate</h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend iconType="rect" wrapperStyle={{ fontSize: '14px' }} />
              <Bar
                barSize={60}
                dataKey="refunds_rate"
                fill={getChartColor(0)}
                name="Refunds Rate (%)"
              />
              {filters.compareToPreviousPeriod && (
                <Bar
                  barSize={60}
                  dataKey="refunds_rate_previous_period"
                  fill={getChartColor(1)}
                  name="Refunds Rate (%) (Previous Period)"
                />
              )}
              {filters.compareToSamePeriodPreviousYear && (
                <Bar
                  barSize={60}
                  dataKey="refunds_rate_previous_year"
                  fill={getChartColor(2)}
                  name="Refunds Rate (%) (Previous Year)"
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={handleExportReport}
            >
              Download Excel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
